import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { MatchedLine } from "@/components/MatchBadge";
import { TruckRoleTag } from "@/components/TruckRoleTag";
import { WhereItIs } from "@/components/WhereItIs";
import { useDonorStageLabel } from "@/lib/vehicleLists";
import { partStatusLabel, partStatusColor } from "@/lib/status";
import { titleCase } from "@/lib/text";
import { MainPhotoCell } from "@/components/MainPhotoCell";
import { PartPhotoCell } from "@/components/PartPhotoCell";
import { Loader2, ChevronRight, SearchX } from "lucide-react";

// The other tab's number (Search S3): the same words, counted. Trucks: the truck search's count only (no rows,
// no facets). Parts: the parts search asked for one row. Neither writes a search record — no box is named.
export function useTabCount(kind, q, params = {}) {
  const [c, setC] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    const v = (q || "").trim();
    setC(null);
    if (!v) return undefined;
    let live = true;
    const ask = kind === "trucks"
      ? api.get("/truck-search", { params: { q: v, count_only: 1 } }).then(({ data }) => ({ count: data.total }))
      : api.get("/search", { params: { q: v, page_size: 1, ...JSON.parse(key) } })
        .then(({ data }) => ({ count: data.parts_total, lowerBound: data.parts_total_is_lower_bound }));
    ask.then((x) => { if (live) setC(x); }).catch(() => { if (live) setC({ count: null, failed: true }); });
    return () => { live = false; };
  }, [kind, q, key]);
  return c;
}

// NEVER A DEAD END (G9): each suggestion was already run, so its count is what one tap shows.
export function DidYouMean({ q, options, onPick, testid = "dym" }) {
  if (!options?.length) return null;
  return (
    <div data-testid={testid} className="text-left">
      <p className="text-base text-slate-700">Did you mean…</p>
      <div className="mt-2 flex flex-col gap-2">
        {options.map((o, i) => (
          <button key={o.q} type="button" data-testid={`${testid}-option-${i}`} onClick={() => onPick(o.q)}
            className="min-h-12 w-full text-left rounded-xl border border-[#E0781A] bg-orange-50 px-4 py-2 hover:bg-orange-100 transition-colors">
            <span className="font-mono font-bold text-lg text-slate-900">{o.q}</span>
            <span className="text-sm text-slate-600"> · {o.count.toLocaleString()} found</span>
            <span data-testid={`${testid}-why-${i}`} className="block text-sm text-slate-500">{o.why}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Nothing found for the parts — what else the same words find: a look-alike or a spelling, the parts in other
// states, and the gone ones. Every number is what the tap shows.
export function PartsDeadEnd({ q, res, onPick, onShowOther, onShowAll, testid = "dead-end" }) {
  const dym = res?.did_you_mean || [];
  const os = res?.other_states;
  const live = os?.live || 0;
  const all = os?.all || 0;
  return (
    <div data-testid={testid} className="space-y-3">
      <p className="font-display font-bold text-slate-800 text-lg">Nothing found for “{q}”</p>
      <DidYouMean q={q} options={dym} onPick={onPick} testid={`${testid}-dym`} />
      {live > 0 && onShowOther && (
        <button type="button" data-testid={`${testid}-other-states`} onClick={onShowOther}
          className="min-h-12 w-full text-left rounded-xl border border-slate-300 bg-white px-4 py-2 text-base font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
          {live.toLocaleString()} in other states — show them
          <span className="block text-sm font-normal text-slate-500">waiting for ID, in review, in a cart, sold… each row says which</span>
        </button>
      )}
      {all > live && onShowAll && (
        <button type="button" data-testid={`${testid}-all-states`} onClick={onShowAll}
          className="min-h-12 w-full text-left rounded-xl border border-slate-300 bg-white px-4 py-2 text-base font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
          Include archived — {all.toLocaleString()} in all
        </button>
      )}
    </div>
  );
}

// THE TRUCKS TAB on Find a Part and All Parts: the trucks the same words find, best first. Tap a row to open it.
export function TruckResults({ q, onOpen, onPick, testid = "truck-results" }) {
  const [res, setRes] = useState(null);
  const stageLabel = useDonorStageLabel();
  useEffect(() => {
    const v = (q || "").trim();
    setRes(null);
    if (!v) return undefined;
    let live = true;
    api.get("/truck-search", { params: { q: v, page_size: 25, facets: 0, hints: 1 } })
      .then(({ data }) => { if (live) setRes({ ...data, for_q: v }); })
      .catch(() => { if (live) setRes({ items: [], total: 0, failed: true, for_q: v }); });
    return () => { live = false; };
  }, [q]);
  if (!res) return <div data-testid={`${testid}-loading`} className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  if (res.failed) return <div data-testid={`${testid}-failed`} className="p-6 text-center text-base text-[#B01E28]">Couldn’t search the trucks — try again.</div>;
  const items = res.items || [];
  if (!items.length) {
    return (
      <div data-testid={`${testid}-none`} className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 p-6 space-y-3">
        <p className="font-display font-bold text-slate-800 text-lg flex items-center gap-2"><SearchX className="w-5 h-5 text-slate-400" />No trucks found for “{q}”</p>
        <DidYouMean q={q} options={res.did_you_mean} onPick={onPick} testid={`${testid}-dym`} />
      </div>
    );
  }
  return (
    <div data-testid={testid}>
      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100">
        {items.map((d, i) => (
          <div key={d.id} role="button" tabIndex={0} data-testid={`${testid}-row-${d.stock_number}`} data-back-row onClick={() => onOpen(d)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(d); } }}
            className={`w-full min-h-12 text-left flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${i === 0 ? "ring-2 ring-inset ring-[#E0781A] bg-orange-50/40" : ""}`}>
            <span onClick={(e) => e.stopPropagation()} className="shrink-0">
              <MainPhotoCell recordId={d.id} stockNumber={d.stock_number} main={d.main_photo} photoCount={d.photo_count || 0}
                countOverlay compactCount size="w-14 h-14" testidPrefix={`${testid}-photo-${d.stock_number}`} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-base font-bold text-slate-900">{d.stock_number}</span>
                <span className="text-base font-medium text-slate-800">{[d.year, d.make, d.model].filter(Boolean).join(" ") || "—"}</span>
                <Badge className="rounded-md bg-slate-800 text-white text-sm font-semibold">{stageLabel(d.status)}</Badge>
                <TruckRoleTag role={d.role} testid={`${testid}-role-${d.stock_number}`} />
              </span>
              <span className="block font-mono text-sm text-slate-500 truncate">{d.vin ? `VIN ${d.vin}` : "No VIN"}{d.engine_model ? ` · ${d.engine_model}` : ""}</span>
              <MatchedLine match={d.match} kind="truck" testid={`${testid}-matched-${d.stock_number}`} />
            </span>
            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
          </div>
        ))}
      </div>
      {res.total > items.length && (
        <Link data-testid={`${testid}-more`} to={`/donors?q=${encodeURIComponent(q)}`}
          className="mt-2 flex items-center justify-center min-h-12 rounded-xl border border-slate-300 bg-white text-base font-semibold text-[#B01E28] hover:bg-slate-50">
          See all {res.total.toLocaleString()} in Vehicles
        </Link>
      )}
    </div>
  );
}

// THE PARTS TAB on Vehicles: Find a Part's answer for the same words — Available first, like Find a Part opens;
// the dead end widens it here, in place (other states, then archived too).
const PART_MODES = { available: { status: "available" }, live: {}, all: { include_archived: 1 } };
const MODE_WORDS = { available: "available only", live: "every state still here", all: "every state, archived too" };

export function PartResults({ q, onOpen, onPick, testid = "part-results" }) {
  const [res, setRes] = useState(null);
  const [mode, setMode] = useState("available");
  useEffect(() => { setMode("available"); }, [q]);
  useEffect(() => {
    const v = (q || "").trim();
    setRes(null);
    if (!v) return undefined;
    let live = true;
    api.get("/search", { params: { q: v, page_size: 25, hints: 1, ...PART_MODES[mode] } })
      .then(({ data }) => { if (live) setRes({ ...data, for_q: v }); })
      .catch(() => { if (live) setRes({ parts: [], parts_total: 0, failed: true, for_q: v }); });
    return () => { live = false; };
  }, [q, mode]);
  if (!res) return <div data-testid={`${testid}-loading`} className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  if (res.failed) return <div data-testid={`${testid}-failed`} className="p-6 text-center text-base text-[#B01E28]">Couldn’t search the parts — try again.</div>;
  const parts = res.parts || [];
  const modeLine = (
    <div data-testid={`${testid}-mode`} className="mb-2 flex items-center gap-2 flex-wrap text-sm text-slate-600">
      Showing {MODE_WORDS[mode]}
      {mode !== "available" && (
        <button type="button" data-testid={`${testid}-mode-reset`} onClick={() => setMode("available")}
          className="min-h-12 px-3 rounded-lg font-semibold text-[#B01E28] hover:bg-red-50">Back to available only</button>
      )}
    </div>
  );
  if (!parts.length) {
    return (
      <div>
        {modeLine}
        <div className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 p-6">
          <PartsDeadEnd q={q} res={res} onPick={onPick} testid={`${testid}-none`}
            onShowOther={() => setMode("live")} onShowAll={() => setMode("all")} />
        </div>
      </div>
    );
  }
  return (
    <div data-testid={testid}>
      {modeLine}
      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100">
        {parts.map((p, i) => (
          <div key={p.id} role="button" tabIndex={0} data-testid={`${testid}-row-${p.short_code}`} data-back-row onClick={() => onOpen(p)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); } }}
            className={`w-full min-h-12 text-left flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-slate-50 ${i === 0 ? "ring-2 ring-inset ring-[#E0781A] bg-orange-50/40" : ""}`}>
            <PartPhotoCell p={p} testidPrefix={`${testid}-photo-${p.short_code}`} />
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-semibold text-slate-900">{titleCase(p.name) || "Unnamed part"}</span>
                <Badge className={`rounded-md text-white text-sm font-semibold ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
                <span className="font-mono text-sm text-slate-500">{p.tag_code || p.short_code}</span>
              </span>
              <MatchedLine match={p.match} testid={`${testid}-matched-${p.short_code}`} />
              <span className="mt-1 flex items-center gap-2 flex-wrap text-sm text-slate-600">
                {p.location && <WhereItIs location={p.location} size="md" testid={`${testid}-where-${p.short_code}`} />}
                {[p.year, p.make, p.model].some(Boolean) && <span>off a {titleCase([p.year, p.make, p.model].filter(Boolean).join(" "))}</span>}
              </span>
            </span>
            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
          </div>
        ))}
      </div>
      <Link data-testid={`${testid}-more`} to={`/search?q=${encodeURIComponent(q)}`}
        className="mt-2 flex items-center justify-center min-h-12 rounded-xl border border-slate-300 bg-white text-base font-semibold text-[#B01E28] hover:bg-slate-50">
        {res.parts_total > parts.length ? `See all ${res.parts_total.toLocaleString()}${res.parts_total_is_lower_bound ? "+" : ""} in Find a Part` : "Open in Find a Part"}
      </Link>
    </div>
  );
}
